import type { MeDto } from '@deli/contracts';
import { createContext, useContext, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useMe } from '../api/staff.js';
import { QueryState } from '../components/QueryState.js';

const MeContext = createContext<MeDto | null>(null);

/** The signed-in user (only inside <RequireAuth>). */
export function useCurrentUser(): MeDto {
  const me = useContext(MeContext);
  if (!me) throw new Error('useCurrentUser outside <RequireAuth>');
  return me;
}

export const hasPermission = (me: MeDto, permission: string): boolean =>
  me.permissions.includes(permission);

/** Renders children for a signed-in user; otherwise sends them to /login (and back afterwards). */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  return (
    <QueryState query={me}>
      {(user) =>
        user ? (
          <MeContext.Provider value={user}>{children}</MeContext.Provider>
        ) : (
          <Navigate to="/login" replace state={{ from: location.pathname }} />
        )
      }
    </QueryState>
  );
}

/** Shows children only when the signed-in user holds the permission (the API enforces it too). */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <>{hasPermission(useCurrentUser(), permission) ? children : fallback}</>;
}

/** Shows children when the signed-in user holds at least one of the permissions. */
export function CanAny({
  permissions,
  children,
  fallback = null,
}: {
  permissions: readonly string[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const me = useCurrentUser();
  return <>{permissions.some((p) => hasPermission(me, p)) ? children : fallback}</>;
}
